// src/components/portal/PortalDebito.js
import React, { useState, useEffect } from "react";
import "./PortalDebito.css";
import { iniciarPagamento } from "../../services/api";

const PortalDebito = () => {
  const [codigo, setCodigo] = useState("");
  const [aluno, setAluno] = useState(null);
  const [debitos, setDebitos] = useState([]);
  const [selectedDebito, setSelectedDebito] = useState(null);
  const [telefone, setTelefone] = useState("");
  const [loading, setLoading] = useState(false);
  const [etapa, setEtapa] = useState(1);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  // 🔹 Busca aluno e débitos
  const handleBuscarAluno = async () => {
    if (!codigo.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/portal/aluno/${codigo}`);
      const data = await response.json();

      if (!response.ok) {
        alert(data.message || "Aluno não encontrado.");
        return;
      }

      // ✅ Garantindo que cada débito tenha referência
      const debitosComReferencia = (data.debitos || []).map((d) => ({
        ...d,
        referencia: d.referencia || `REF${d.id}` // fallback caso backend não forneça
      }));

      setAluno(data.aluno);
      setDebitos(debitosComReferencia);
      setEtapa(2);
    } catch (err) {
      alert("Erro ao buscar aluno.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Abre modal antes de enviar pagamento
  const handleAbrirConfirmacao = () => {
    if (!selectedDebito || !telefone) {
      alert("Selecione um débito e informe o telefone.");
      return;
    }
    setMostrarConfirmacao(true);
  };

  // 🔹 Envia pagamento M-Pesa
  const enviarPagamento = async () => {
    if (!selectedDebito || !telefone) {
      alert("Dados inválidos para pagamento.");
      return;
    }

    setMostrarConfirmacao(false);
    setEtapa(3);
    setLoading(true);

    try {
      const resultado = await iniciarPagamento({
        debito_id: selectedDebito.id,
        tipo: selectedDebito.tipo,
        telefone,
        referencia: selectedDebito.referencia // ✅ Passando referência correta
      });

      if (
        resultado?.mpesaResponse &&
        resultado.mpesaResponse.output_ResponseCode !== "INS-0"
      ) {
        alert("Erro ao iniciar pagamento no M-Pesa.");
        setEtapa(2); // volta para seleção
        return;
      }

      // 🔹 Polling para verificar status do pagamento
      const ref = selectedDebito.referencia;
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`/api/portal/pagamento/status?ref=${ref}`);
          const data = await res.json();

          if (data.status === "CONFIRMADO") {
            clearInterval(interval);
            setPollingInterval(null);
            alert("✅ Pagamento confirmado!");
            setEtapa(4); // ou voltar para lista de débitos
          } else if (data.status === "FALHOU") {
            clearInterval(interval);
            setPollingInterval(null);
            alert("❌ Pagamento falhou.");
            setEtapa(2);
          }
        } catch (err) {
          console.error("Erro ao verificar status do pagamento:", err);
        }
      }, 3000);

      setPollingInterval(interval);
    } catch (error) {
      console.error("Erro ao enviar pagamento:", error);
      alert("Erro ao comunicar com o servidor.");
      setEtapa(2);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    setSelectedDebito(null);
    setTelefone("");
    setMostrarConfirmacao(false);
  };

  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  return (
    <div className="portal-page">
      <header className="portal-header">
        <h1>ACADE-SGE</h1>
      </header>

      <div className="portal-content">
        <div className="portal-welcome">
          <h2>Bem-vindo ao Portal de Pagamentos da Escola Vida Abundante</h2>
          <p>
            Escolha os débitos e <span className="mpesa-text">pague via M-Pesa.</span>
          </p>
        </div>

        <div className="portal-box-container">
          {etapa === 1 && (
            <div className="portal-box">
              <h2>Digite o código do aluno</h2>
              <input
                type="text"
                placeholder="Ex: 20260001"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
              />
              <button onClick={handleBuscarAluno} disabled={loading}>
                {loading ? "A verificar..." : "Continuar"}
              </button>
            </div>
          )}

          {etapa === 2 && aluno && (
            <div className="portal-box">
              <h2>Confirme os dados</h2>
              <div className="aluno-info">
                <p>
                  <span className="label">Código:</span>
                  <span className="valor">{aluno.codigo_aluno}</span> |
                  <span className="label"> Nome:</span>
                  <span className="valor nome">{aluno.nome}</span> |
                  <span className="label"> Classe:</span>
                  <span className="valor">{aluno.classe}</span> |
                  <span className="label"> Turma:</span>
                  <span className="valor">{aluno.turma}</span>
                </p>
              </div>

              <h3>Débitos Pendentes</h3>
              <div className="debitos-grid">
                {debitos.map((debito) => {
                  const key = `${debito.id}_${debito.tipo}`;
                  return (
                    <label
                      key={key}
                      className={`debito-card ${
                        selectedDebito?.id === debito.id &&
                        selectedDebito?.tipo === debito.tipo
                          ? "selected"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="debito"
                        onChange={() =>
                          setSelectedDebito({
                            ...debito
                          })
                        }
                      />
                      <div className="debito-info">
                        <h4>{debito.descricao}</h4>
                        <p>
                          Valor: <strong>{debito.total} MZN</strong>
                          {debito.valor_multa > 0 && (
                            <span className="multa">
                              {" "} (+{debito.valor_multa} MZN multa)
                            </span>
                          )}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>

              <input
                type="text"
                placeholder="Número M-Pesa (84xxxxxxx)"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
              />

              <div className="button-group">
                <button onClick={handleAbrirConfirmacao} disabled={loading}>
                  {loading ? "Processando..." : "Confirmar Pagamento"}
                </button>
                <button onClick={handleCancelar} className="cancel-button">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {etapa === 3 && (
            <div className="portal-box">
              <h2 className="loading-text">
                <span className="spinner"></span>
                Aguardando confirmação...
              </h2>
              <p className="texto-confirmacao">
                Verifique o seu telemóvel e insira o <span className="mpesa-text">PIN M-Pesa.</span>
              </p>
            </div>
          )}

          {etapa === 4 && (
            <div className="portal-box">
              <h2>Pagamento concluído!</h2>
              <p>O pagamento foi registrado com sucesso no sistema.</p>
            </div>
          )}
        </div>
      </div>

      {/* 🔹 MODAL DE CONFIRMAÇÃO */}
      {mostrarConfirmacao && selectedDebito && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Confirmação de Pagamento</h3>
            <p>
              Tens a certeza que pretende pagar{" "}
              <strong>{selectedDebito.total} MZN</strong>{" "}
              referentes a{" "}
              <strong>{selectedDebito.descricao}</strong>{" "}
              para o aluno <strong>{aluno.nome}</strong>?
            </p>

            <div className="button-group">
              <button onClick={enviarPagamento} disabled={loading}>
                {loading ? "A enviar..." : "Confirmar"}
              </button>

              <button
                className="cancel-button"
                onClick={handleCancelar}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="portal-footer">
        <p>© 2024-2025 Desenvolvido por Chaúca Creative, Lda.</p>
      </footer>
    </div>
  );
};

export default PortalDebito;